import styled, { keyframes, css } from "styled-components";

// ─── Loading: scan line travels top → bottom ─────────────────────────────────

const scanSweep = keyframes`
  0%   { transform: translateY(-120%); opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translateY(120%); opacity: 0; }
`;

const borderBreathe = keyframes`
  0%, 100% {
    border-color: rgba(var(--glass-white-rgb), 0.75);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.6),
      inset 0 0 14px rgba(var(--color-brand-300-rgb), 0.25),
      0 10px 24px rgba(var(--color-brand-600-rgb), 0.28);
  }
  50% {
    border-color: var(--glass-edge-strong);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.85),
      inset 0 0 22px rgba(var(--color-brand-300-rgb), 0.4),
      0 14px 32px rgba(var(--color-brand-600-rgb), 0.42);
  }
`;

// ─── Idle: gentle glow pulse on BLUE variant ─────────────────────────────────

const idleGlow = keyframes`
  0%, 100% {
    box-shadow:
      0 14px 28px rgba(var(--color-brand-600-rgb), 0.32),
      0 3px 8px rgba(var(--color-brand-600-rgb), 0.14),
      inset 0 1px 0 rgba(255, 255, 255, 0.6),
      inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.28);
  }
  50% {
    box-shadow:
      0 18px 36px rgba(var(--color-brand-600-rgb), 0.46),
      0 4px 10px rgba(var(--color-brand-600-rgb), 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.36);
  }
`;

// ─── Idle: gentle glow pulse on WHITE variant ───────────────────────────────

const idleGlowWhite = keyframes`
  0%, 100% {
    box-shadow:
      0 8px 20px rgba(var(--color-brand-500-rgb), 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.24);
  }
  50% {
    box-shadow:
      0 12px 26px rgba(var(--color-brand-500-rgb), 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.34);
  }
`;

// ─── Idle: soft light beam glides across the button every few seconds ───────

const idleShimmer = keyframes`
  0%, 66% {
    transform: translateX(-110%) skewX(-14deg);
    opacity: 0;
  }
  74% {
    opacity: 0.9;
  }
  90% {
    opacity: 0.9;
  }
  100% {
    transform: translateX(210%) skewX(-14deg);
    opacity: 0;
  }
`;

// ─── Splash: button squishes & spits droplets outward ────────────────────────

const buttonSplash = keyframes`
  0% {
    transform: scale(1);
  }
  18% {
    transform: scale(0.96);
    border-color: rgba(var(--color-brand-300-rgb), 0.65);
    box-shadow:
      inset 0 2px 6px rgba(var(--color-brand-400-rgb), 0.28),
      0 0 0 2px rgba(var(--color-brand-400-rgb), 0.2),
      0 0 20px rgba(var(--color-brand-400-rgb), 0.42);
  }
  40% {
    transform: scale(1.04);
    border-color: rgba(var(--glass-pale-rgb), 0.9);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 0 24px rgba(255, 255, 255, 0.3),
      0 0 0 3px rgba(var(--color-brand-400-rgb), 0.3),
      0 0 36px rgba(var(--color-brand-600-rgb), 0.62);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 14px rgba(var(--color-brand-400-rgb), 0.24);
  }
  100% {
    transform: scale(1);
  }
`;

const contentBob = keyframes`
  0% {
    transform: scale(1) translateY(0);
    text-shadow: none;
  }
  18% {
    transform: scale(0.94) translateY(1px);
    text-shadow: none;
  }
  40% {
    transform: scale(1.06) translateY(-2px);
    text-shadow:
      0 0 12px rgba(var(--glass-white-rgb), 0.85),
      0 0 22px rgba(var(--color-brand-400-rgb), 0.65);
  }
  100% {
    transform: scale(1) translateY(0);
    text-shadow: none;
  }
`;

// ─── Droplet: emoji shoots outward along a parabolic arc ─────────────────────

const dropletFly = keyframes`
  0% {
    transform: translate(-50%, -50%) translate(0, 0) scale(0.3) rotate(0deg);
    opacity: 0;
  }
  18% {
    opacity: 1;
    transform: translate(-50%, -50%)
      translate(calc(var(--dx) * 0.28), calc(var(--dy) * 0.28 - 6px))
      scale(1.15)
      rotate(calc(var(--rot) * 0.3));
  }
  60% {
    opacity: 1;
    transform: translate(-50%, -50%)
      translate(calc(var(--dx) * 0.7), calc(var(--dy) * 0.7 - 4px))
      scale(1)
      rotate(calc(var(--rot) * 0.7));
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%)
      translate(var(--dx), calc(var(--dy) + 8px))
      scale(0.55)
      rotate(var(--rot));
  }
`;

// ─── Components ──────────────────────────────────────────────────────────────

export const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
  isolation: isolate;
`;

export const ScanLine = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 999px;
  pointer-events: none;
  overflow: hidden;
  z-index: 2;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1.5px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(var(--glass-white-rgb), 0.95) 50%,
      transparent 100%
    );
    box-shadow: 0 0 10px rgba(var(--color-brand-400-rgb), 0.85);
    animation: ${scanSweep} 1.4s ease-in-out infinite;
  }
`;

export const SplashLayer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 3;
`;

export const Droplet = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  font-size: ${(p) => p.$size || 22}px;
  line-height: normal;
  filter: drop-shadow(0 2px 6px rgba(var(--color-brand-600-rgb), 0.55))
    drop-shadow(0 0 10px rgba(var(--color-brand-300-rgb), 0.45));
  pointer-events: none;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0);
  animation: ${dropletFly} ${(p) => p.$duration || 720}ms
    cubic-bezier(0.22, 0.7, 0.3, 1) forwards;
  animation-delay: ${(p) => p.$delay || 0}ms;
`;

export const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
  z-index: 1;

  ${(p) =>
    p.$isExploding &&
    css`
      animation: ${contentBob} 0.55s cubic-bezier(0.4, 0, 0.4, 1) forwards;
    `}
`;

export const Button = styled.button`
  position: relative;
  display: flex;
  height: ${(p) => (p.$small ? "40px" : "46px")};
  width: 100%;
  padding: 0 24px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  isolation: isolate;
  border-radius: 999px;
  font-family: var(--font-family-primary);
  font-size: ${(p) => (p.$small ? "14px" : "16px")};
  font-weight: 700;
  letter-spacing: -0.01em;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  /* ===== WHITE VARIANT — frosted glass pill with brand-gradient text ===== */
  ${(p) =>
    p.$white &&
    css`
      border: 1px solid var(--glass-edge);
      background:
        radial-gradient(
          120% 100% at 20% 0%,
          rgba(var(--glass-white-rgb), 0.98) 0%,
          rgba(var(--glass-pale-rgb), 0.88) 55%,
          rgba(var(--glass-pale-rgb), 0.8) 100%
        ),
        linear-gradient(
          180deg,
          rgba(var(--glass-white-rgb), 0.94) 0%,
          rgba(var(--glass-pale-rgb), 0.88) 100%
        );
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      box-shadow:
        0 8px 20px rgba(var(--color-brand-500-rgb), 0.18),
        inset 0 1px 0 rgba(255, 255, 255, 1),
        inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.24);
      color: transparent;
      background-clip: padding-box;

      [data-theme="dark"] & {
        border: none;
        box-shadow:
          0 8px 18px rgba(0, 0, 0, 0.5),
          inset 0 1px 0 rgba(255, 255, 255, 0.16),
          inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.18);
      }

      /* Gradient-clipped text via a child span */
      ${ButtonContent} {
        background: linear-gradient(
          180deg,
          var(--color-brand-400) 0%,
          var(--color-brand-600) 55%,
          var(--color-brand-700) 100%
        );
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    `}

  /* ===== DEFAULT (BLUE) VARIANT — brand gradient pill with white text ===== */
  ${(p) =>
    !p.$white &&
    css`
      border: 1px solid rgba(var(--glass-white-rgb), 0.72);
      background:
        radial-gradient(
          140% 100% at 20% 0%,
          rgba(255, 255, 255, 0.3) 0%,
          rgba(255, 255, 255, 0) 55%
        ),
        linear-gradient(
          180deg,
          var(--color-brand-400) 0%,
          var(--color-brand-600) 55%,
          var(--color-brand-700) 100%
        );
      color: var(--glass-text-on-pill);
      box-shadow:
        0 14px 28px rgba(var(--color-brand-600-rgb), 0.32),
        0 3px 8px rgba(var(--color-brand-600-rgb), 0.14),
        inset 0 1px 0 rgba(255, 255, 255, 0.6),
        inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.28);

      [data-theme="dark"] & {
        /* Keep the brand-blue gradient pill in dark theme (matches the
           credit/CTA language) — just drop the bright white edge so it
           reads as a premium aqua pill on the deep navy base, not as a
           lit-up beacon. */
        border: none;
        border-color: transparent;
        box-shadow:
          0 12px 24px rgba(0, 0, 0, 0.55),
          inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.32);
      }
    `}

  /* Top sheen reflection */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, ${(p) => (p.$white ? 0.8 : 0.65)}) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Moving shimmer band */
  &::after {
    content: "";
    position: absolute;
    top: -2px;
    bottom: -2px;
    left: 0;
    width: 55%;
    background: ${(p) =>
      p.$white
        ? `linear-gradient(
            100deg,
            transparent 0%,
            rgba(var(--color-brand-300-rgb), 0.18) 28%,
            rgba(var(--color-brand-400-rgb), 0.38) 45%,
            rgba(var(--color-brand-400-rgb), 0.52) 50%,
            rgba(var(--color-brand-400-rgb), 0.38) 55%,
            rgba(var(--color-brand-300-rgb), 0.18) 72%,
            transparent 100%
          )`
        : `linear-gradient(
            100deg,
            transparent 0%,
            rgba(var(--glass-white-rgb), 0.15) 28%,
            rgba(var(--glass-white-rgb), 0.38) 45%,
            rgba(var(--glass-white-rgb), 0.55) 50%,
            rgba(var(--glass-white-rgb), 0.38) 55%,
            rgba(var(--glass-white-rgb), 0.15) 72%,
            transparent 100%
          )`};
    filter: blur(2px);
    pointer-events: none;
    transform: translateX(-110%) skewX(-14deg);
    opacity: 0;
    z-index: 0;
    mix-blend-mode: ${(p) => (p.$white ? "multiply" : "overlay")};
  }

  [data-theme="dark"] &::after {
    opacity: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: ${(p) =>
      p.$white
        ? "rgba(var(--color-brand-400-rgb), 0.45)"
        : "rgba(var(--glass-white-rgb), 0.95)"};
    filter: brightness(1.04);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    filter: grayscale(0.1);
  }

  /* === IDLE STATE: gentle glow + periodic shimmer sweep === */
  ${(p) =>
    !p.$isLoading &&
    !p.$isExploding &&
    !p.disabled &&
    css`
      animation: ${p.$white ? idleGlowWhite : idleGlow} 3.6s ease-in-out
        infinite;

      &::after {
        animation: ${idleShimmer} 4.4s ease-in-out infinite;
      }

      /* In dark theme the bright brand-glow keyframes paint a pulsing
         halo that overpowers the page. Pin a calm, deep static shadow
         instead. */
      [data-theme="dark"] & {
        animation: none;
        box-shadow:
          0 10px 22px rgba(0, 0, 0, 0.55),
          0 3px 8px rgba(0, 0, 0, 0.32),
          inset 0 1px 0 rgba(255, 255, 255, 0.18),
          inset 0 -3px 6px rgba(0, 0, 0, 0.22);
      }
    `}

  /* === LOADING STATE === */
  ${(p) =>
    p.$isLoading &&
    css`
      animation: ${borderBreathe} 1.6s ease-in-out infinite;
    `}

  /* === SPLASH (exploding) STATE === */
  ${(p) =>
    p.$isExploding &&
    css`
      animation: ${buttonSplash} 0.55s cubic-bezier(0.4, 0, 0.4, 1) forwards;
    `}
`;
